import shallowMerge from "../../utils/shallowMerge";
import { ActionTypes } from "../actions";
import coalesce from "../../utils/coalesce";
const DefaultBreakpointID = {
  Desktop: "desktop",
  Tablet: "tablet",
  Mobile: "mobile"
};
const DEFAULT_BREAKPOINTS = [
  {
    id: DefaultBreakpointID.Desktop,
    label: "Desktop",
    minWidth: 769
  },
  {
    id: DefaultBreakpointID.Tablet,
    label: "Tablet",
    minWidth: 576,
    maxWidth: 768,
    viewportWidth: 760
  },
  {
    id: DefaultBreakpointID.Mobile,
    label: "Mobile",
    maxWidth: 575,
    viewportWidth: 390
  }
];
function getInitialState(breakpoints = DEFAULT_BREAKPOINTS) {
  return breakpoints;
}
function reducer(state = getInitialState(), action) {
  switch (action.type) {
    case ActionTypes.SET_BREAKPOINTS: {
      const breakpoints = action.payload.breakpoints;
      if (breakpoints.length === 0)
        throw new Error("Breakpoints cannot be empty.");
      return breakpoints;
    }
    default:
      return state;
  }
}
function parseBreakpointsInput(input) {
  validateBreakpointsInput(input);
  const sorted = Object.entries(input).map(([id, value]) => ({ ...value, id })).sort((a, b) => b.width - a.width);
  const transformed = sorted.reduce(
    (prev, curr, index, array) => {
      const { width, viewport, id, label } = curr;
      const next = array[index + 1];
      const breakpoint = {
        id,
        ...label && { label },
        ...next && { minWidth: next.width + 1 },
        maxWidth: width,
        viewportWidth: viewport ?? width
      };
      return [...prev, breakpoint];
    },
    [
      { id: DefaultBreakpointID.Desktop, label: "Desktop", minWidth: sorted[0].width + 1 }
    ]
  );
  return transformed;
}
function validateBreakpointsInput(input) {
  if (DefaultBreakpointID.Desktop in input) {
    throw new Error(
      `Cannot change the base breakpoint. "${DefaultBreakpointID.Desktop}" is reserved as the base breakpoint.`
    );
  }
  if (Object.keys(input).length === 0) {
    throw new Error(`Breakpoints cannot be empty. You must provide at least one breakpoint.`);
  }
  const sorted = Object.entries(input).map(([id, value]) => ({ ...value, id })).sort((a, b) => b.width - a.width);
  sorted.forEach(({ id, width, viewport }, index, array) => {
    if (viewport && viewport > width) {
      throw new Error(
        `Error on breakpoint "${id}". Viewport cannot be greater than its width.
        "${id}" has a viewport of ${viewport}px and a width of ${width}px.`
      );
    }
    const next = array[index + 1];
    if (viewport && next && viewport < next.width) {
      throw new Error(
        `Error on breakpoint "${id}". Viewport cannot be smaller than the next breakpoint's width. 
        "${id}" has a viewport of ${viewport}px and the next breakpoint "${next.id}" has a width of ${next.width}px.`
      );
    }
    if (next && width === next.width) {
      throw new Error(
        `Breakpoints cannot have the same width. "${id}" and "${next.id}" have the same width`
      );
    }
  });
}
function sortBreakpoints(breakpoints) {
  return breakpoints.sort((a, b) => (b?.minWidth ?? 0) - (a?.minWidth ?? 0));
}
const getBreakpoint = (state, breakpointId) => {
  const breakpoint = state.find(({ id }) => id === breakpointId);
  if (breakpoint == null)
    throw new Error(`Unrecognized breakpoint ID: "${breakpointId}".`);
  return breakpoint;
};
const getBaseBreakpoint = (breakpoints) => {
  const breakpoint = sortBreakpoints(breakpoints)[0];
  if (breakpoint == null)
    throw new Error(`Cannot get base breakpoint.`);
  return breakpoint;
};
function findBreakpointOverride(breakpoints, values = [], deviceId, strategy = defaultStrategy) {
  const value = values.find(({ deviceId: d }) => d === deviceId);
  const fallbacks = breakpoints.slice(0, breakpoints.findIndex((d) => d.id === deviceId) + 1).reverse().map((d) => values.find((v) => v.deviceId === d.id)).filter((override) => Boolean(override));
  return value != null || fallbacks.length > 0 ? strategy(value, fallbacks) : void 0;
}
function defaultStrategy(value, fallbacks) {
  return value || fallbacks[0];
}
function shallowMergeFallbacks(value, fallbacks) {
  return [value, ...fallbacks].filter((override) => Boolean(override)).reduce((a, b) => ({
    deviceId: a.deviceId || b.deviceId,
    value: shallowMerge(a.value, b.value)
  }));
}
function mergeOrCoalesce(a, b) {
  if (typeof a === "object" && a !== null && !Array.isArray(a) && typeof b === "object" && b !== null && !Array.isArray(b)) {
    return shallowMerge(a, b);
  }
  return coalesce(a, b);
}
function mergeOrCoalesceFallbacks(value, fallbacks) {
  return [value, ...fallbacks].filter((override) => Boolean(override)).reduce((a, b) => ({
    deviceId: a.deviceId || b.deviceId,
    value: mergeOrCoalesce(a.value, b.value)
  }));
}
function join(breakpoints, responsiveValues, joinFn, strategy) {
  return breakpoints.map(({ id }) => id).map((deviceId) => {
    const value = joinFn(
      responsiveValues.map((responsiveValue) => {
        const deviceValue = responsiveValue && findBreakpointOverride(breakpoints, responsiveValue, deviceId, strategy);
        return deviceValue == null ? void 0 : deviceValue.value;
      })
    );
    if (value == null)
      return null;
    return { deviceId, value };
  }).filter((override) => Boolean(override));
}
const getBreakpointMediaQuery = (breakpoint) => {
  const parts = ["@media only screen"];
  if (breakpoint.minWidth != null) {
    parts.push(`(min-width: ${breakpoint.minWidth}px)`);
  }
  if (breakpoint.maxWidth != null) {
    parts.push(`(max-width: ${breakpoint.maxWidth}px)`);
  }
  return parts.join(" and ");
};
const getViewportStyle = (state, deviceId) => {
  const device = getBreakpoint(state, deviceId);
  return device && {
    width: device.viewportWidth != null ? device.viewportWidth : "100%",
    minWidth: device.minWidth
  };
};
function findNextFallback(breakpoints, value, deviceId, activeDeviceId, fallbackStrategy) {
  const deviceOverride = findBreakpointOverride(
    breakpoints,
    value.filter((v) => v.deviceId !== activeDeviceId),
    deviceId,
    fallbackStrategy
  );
  return (deviceOverride && getBreakpoint(breakpoints, deviceOverride.deviceId)) ?? null;
}
const mergeResponsiveValues = (breakpoints, source, override) => {
  const devices = [
    ...new Set(
      source.map(({ deviceId }) => deviceId).concat(override.map(({ deviceId }) => deviceId))
    )
  ];
  return devices.map((deviceId) => ({
    deviceId,
    value: {
      ...(findBreakpointOverride(breakpoints, source, deviceId) || { value: {} }).value,
      ...(findBreakpointOverride(breakpoints, override, deviceId, (v) => v) || { value: {} }).value
    }
  }));
};
export {
  DEFAULT_BREAKPOINTS,
  DefaultBreakpointID,
  findBreakpointOverride,
  findNextFallback,
  getBaseBreakpoint,
  getBreakpoint,
  getBreakpointMediaQuery,
  getInitialState,
  getViewportStyle,
  join,
  mergeOrCoalesceFallbacks,
  mergeResponsiveValues,
  parseBreakpointsInput,
  reducer,
  shallowMergeFallbacks
};
//# sourceMappingURL=breakpoints.js.map